// @ts-nocheck
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Copy,
  Facebook,
  HeartPulse,
  Images,
  Instagram,
  LayoutGrid,
  PenLine,
  Link2,
  ListTodo,
  MessageCircle,
  MessageSquare,
  Palette,
  Pause,
  Play,
  RefreshCw,
  Settings,
  ShoppingBag,
  Sparkles,
  StickyNote,
  Trash2,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { api, CarouselItem, type CarouselInspirationBorrow, type CarouselLayoutQa } from "@/lib/api";
import BgPresetPicker from "@/components/BgPresetPicker";
import BgPlacementPicker from "@/components/BgPlacementPicker";
import LayoutTemplateBar from "@/components/LayoutTemplateBar";
import {
  buildTemplatePatchFromPreset,
  committedPresetId,
} from "@/lib/carouselStyleApply";
import { copyRenderBlockedReason } from "@/lib/studioVoice";
import { TONE_CLASS, type SettingsTone } from "@/lib/carouselSettingsMeta";
import { copyText, packFromItem } from "@/lib/carouselGenerationPack";
import {
  recommendCta,
  SOURCE_EMPTY,
  STATUS_TIPS,
  STUDIO_TAB_META,
  STUDIO_TIPS,
  houseCanonState,
  studioTabFromHash,
  studioTabToHash,
  type AbVariable,
  type BgMode,
  type CloseMode,
  type CtaIntent,
  type StudioTab,
} from "@/lib/carouselStudioMeta";
import CaptionChatPanel from "@/components/CaptionChatPanel";
import { platformEnding } from "@/lib/carouselCaptionCompose";
import { type ConceptsTraceOutgoing } from "@/lib/carouselGenerationPack";
import PageHeader from "@/components/PageHeader";
import AiJobModal, { type AiJobKind, type AiJobParam } from "@/components/AiJobModal";
import { AiActionButton, CtaIo } from "@/components/studioTabs";
import SlideComposePanel from "@/components/SlideComposePanel";
import { StudioModalBody, StudioModalFooter, StudioModalFrame, StudioModalHeader } from "@/components/StudioModalFrame";
import { DesignAnalysisCard, DesignLayoutNoteCard, HouseCanonBar, StudioOptionCard } from "@/components/DesignReviewPanel";
import LiveSlideCarousel from "@/components/LiveSlideCarousel";
import RenderJobInline from "@/components/RenderJobInline";
import CarouselReelPanel from "@/components/CarouselReelPanel";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { emitInlineMarkdown, type TextMark } from "@/lib/carouselTextMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InfoTip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";


import * as Helpers from "../helpers";
import CzasotekstPanel from "@/components/CzasotekstPanel";
import PostAtelierPanel from "@/components/PostAtelierPanel";
const { ideaDescription, firstSlideField, isHeadlineSlide, slideHasCopy, previewMarks, CopyMarkdownCheat, CopyMarkdownField, flattenSlide, FinalSlidesModal, SourceSection, SourceHits, InspirationPanel, Chip, ChoiceCard, CopyPackButton, FieldLabel, TipBadge, CostFooter, tabForExisting, CarouselQueueList, QueueRow } = Helpers;

export default function RenderTab({ ctx }: { ctx: any }) {
  const {
    BORROW_CHIPS,
    DEFAULT_BORROW,
    STATUS_LABEL,
    abPairId,
    abVariable,
    abortLlmM,
    activeMode,
    aiAbort,
    aiError,
    aiFailed,
    aiJob,
    aiJobParams,
    aiStatusLabel,
    analyzeInspM,
    approveM,
    askDelete,
    bgHint,
    bgPresetId,
    bgPlacementId,
    styleApplyMode,
    bgMode,
    book,
    bookHits,
    bookSectionHits,
    borrow,
    borrowM,
    briefBody,
    cancelRenderM,
    caption,
    captionFeedback,
    captionJobSteps,
    captionM,
    captionChatUndoM,
    captionOnlyJob,
    captionRunning,
    catalog,
    catalogLabel,
    catalogQ,
    catalogTypes,
    changeTrack,
    clickAi,
    closeMode,
    commentHits,
    commitCopyField,
    composeIndex,
    composeMode,
    composeModeM,
    composeSlide,
    conceptsRunning,
    copyApproved,
    copyBusy,
    copyFeedback,
    copyMdRef,
    copyReviseRunning,
    copyRunning,
    copySlides,
    createM,
    ctaIntent,
    ctaM,
    ctaTouched,
    currentQ,
    deleteM,
    designApplyM,
    designApplyRunning,
    designFeedbackM,
    designFixM,
    designFixRunning,
    designQa,
    designReviewM,
    designReviewRunning,
    draftText,
    effectiveClose,
    effectiveIntent,
    finalPreview,
    forcePick,
    formatId,
    fromDraftM,
    fromDraftRunning,
    fromPubM,
    funnel,
    generateCopyM,
    goTab,
    hasComposedSlides,
    hasCopySlides,
    hasSelectedIdea,
    houseCanon,
    inspirationRunning,
    isAiTrack,
    isOperator,
    item,
    itemMode,
    jobRunning,
    layoutPlanM,
    layoutPlanRunning,
    layoutQa,
    layoutQaM,
    layoutQaRunning,
    listQ,
    llmIs,
    mergeCopySlides,
    messengerHits,
    musicStatusQ,
    note,
    openExisting,
    pain,
    patchCopyM,
    pauseM,
    pickTrack,
    proposeMusicQ,
    publishBusy,
    publishRunning,
    qaBlocks,
    qaFailed,
    qaLabel,
    qaOverride,
    qc,
    rankingQ,
    ratingM,
    recCta,
    recomposeM,
    reelM,
    reelRunning,
    renderM,
    renderRunning,
    resetStudio,
    retroM,
    retroRunning,
    retryPublishM,
    reviseCopyM,
    scheduleM,
    searchMusicM,
    selectM,
    selectedId,
    selectedIdea,
    setAbPairId,
    setAbVariable,
    setAiJob,
    setBgHint,
    setBgPresetId,
    setBgPlacementId,
    setStyleApplyMode,
    setBgMode,
    setBook,
    setBorrow,
    setCaption,
    setCaptionFeedback,
    setCloseMode,
    setComposeIndex,
    setComposeMode,
    setCopyFeedback,
    setCopySlides,
    setCtaIntent,
    setCtaTouched,
    setDraftText,
    setFinalPreview,
    setForcePick,
    setFormatId,
    setFunnel,
    setNote,
    setPain,
    setQaOverride,
    setSelectedId,
    setSku,
    setStudioTab,
    setStyleId,
    setTool,
    setTraceId,
    setTraceOut,
    setUrls,
    setUseBook,
    setUseComments,
    setUseMessenger,
    setUseStories,
    setVerifyCta,
    setWhen,
    showPicker,
    sku,
    slot,
    slotQ,
    sourceHits,
    sourcesQ,
    startConcepts,
    startPost,
    statusQ,
    storyHits,
    studio,
    studioTab,
    styleId,
    templateM,
    tool,
    traceId,
    traceItem,
    traceOut,
    uploadInspM,
    urls,
    useBook,
    useComments,
    useMessenger,
    useStories,
    verifyCta,
    voiceHits,
    when
  } = ctx;
  const [pickedPresetId, setPickedPresetId] = useState<string | null>(null);
  useEffect(() => {
    setPickedPresetId(null);
  }, [selectedId, item?.bg_preset_id, item?.style_id, item?.visual_pack_id, item?.style_apply_mode]);
  if (
    item?.kind === "czasotekst" ||
    (item as { content?: { kind?: string } } | undefined)?.content?.kind === "czasotekst"
  ) {
    return (
      <TabsContent value="render" className="mt-0">
        <CzasotekstPanel
          item={item}
          caption={caption}
          section="compose"
          onCaption={setCaption}
          onHook={() => undefined}
        />
      </TabsContent>
    );
  }
  if (
    item?.kind === "feed_post" ||
    (item as { content?: { kind?: string } } | undefined)?.content?.kind === "feed_post"
  ) {
    return (
      <TabsContent value="render" className="mt-0 space-y-4">
        <PostAtelierPanel item={item} />
        <p className="text-xs text-muted-foreground">
          Biblioteka elementów: <Link className="underline" to="/elementy">/elementy</Link>
        </p>
      </TabsContent>
    );
  }
  const activeStyleMode =
    ((item?.style_apply_mode as string) || styleApplyMode) as "bg" | "full" | "layout";
  const committedStylePresetId = committedPresetId(
    activeStyleMode,
    catalog?.bg_presets || [],
    item?.bg_preset_id || bgPresetId || "",
    item?.style_id,
    item?.visual_pack_id,
    item?.layout_preset_id,
  );
  const galleryPresetId = pickedPresetId ?? committedStylePresetId;
  const stylePresetDirty = pickedPresetId !== null && pickedPresetId !== committedStylePresetId;
  const slideTotal = Math.max(
    Number(item?.render_total || 0),
    (item?.slides || []).length,
    Array.isArray(copySlides) ? copySlides.length : 0,
    Number((item?.copy as { slides?: unknown[] } | undefined)?.slides?.length || 0),
  );
  const showLiveCarousel = Boolean(renderRunning || (item?.slides || []).length > 0);
  const renderTabJob =
    aiJob === "render" ||
    aiJob === "houseFix" ||
    aiJob === "designReview" ||
    aiJob === "designApply" ||
    aiJob === "layoutQa"
      ? (aiJob as AiJobKind)
      : renderRunning
        ? ("render" as AiJobKind)
        : designFixRunning
          ? ("houseFix" as AiJobKind)
          : designReviewRunning
            ? ("designReview" as AiJobKind)
            : designApplyRunning
              ? ("designApply" as AiJobKind)
              : layoutQaRunning
                ? ("layoutQa" as AiJobKind)
                : null;
  const renderTabJobRunning = Boolean(
    renderRunning || designFixRunning || designReviewRunning || designApplyRunning || layoutQaRunning,
  );
  const toolsDimmed = renderTabJobRunning;
  return (
        <TabsContent value="render" className="mt-0">
        {composeIndex != null && item && composeSlide?.id && !renderRunning ? (
          <div className="flex min-h-[70vh] min-w-0 gap-4">
            <aside className="flex w-[19.5rem] shrink-0 flex-col self-start rounded-lg border border-border bg-card">
              <div className="shrink-0 border-b border-border px-3 py-2">
                <p className="text-xs font-medium text-foreground">Slajdy</p>
                <p className="text-[10px] text-muted-foreground">Kliknij, żeby przełączyć</p>
              </div>
              {/* Szerokość 19.5rem; wysokość 2 kafelki, cap pod viewport (3 nie mieszczą się z chrome). */}
              <div
                className="space-y-2 overflow-y-auto overscroll-contain p-2"
                style={{
                  height:
                    "min(calc(2 * ((19.5rem - 1rem) * 5 / 4 + 1.4rem) + 0.5rem), calc(100dvh - 12rem))",
                }}
              >
                {(item.slides || []).map((s) => {
                  const active = Number(s.index_n) === Number(composeIndex);
                  return (
                    <button
                      key={s.id || s.index_n}
                      type="button"
                      className={cn(
                        "block w-full overflow-hidden rounded-md border text-left transition",
                        active ? "border-brand ring-2 ring-brand/30" : "border-border hover:border-brand/40",
                      )}
                      onClick={() => setComposeIndex(s.index_n)}
                    >
                      <img
                        src={`${api.carouselSlidePng(item.id, s.index_n)}?v=${item.render_token || item.updated_at || item.render_done || ""}-${s.index_n}`}
                        alt={`slajd ${s.index_n}`}
                        className="aspect-[4/5] w-full bg-muted object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.opacity = "0.25";
                        }}
                      />
                      <span className="block truncate px-1.5 py-1 text-[11px] text-muted-foreground">
                        {s.index_n}. {s.slide_type || "slajd"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>
            <div className="min-w-0 flex-1 overflow-hidden">
              <SlideComposePanel
                key={composeSlide.id || composeSlide.index_n}
                item={item}
                slide={composeSlide}
                fallback={!studio?.gemini_ready || (item?.bg_mode || bgMode || "flat") === "flat"}
                geminiReady={Boolean(studio?.gemini_ready)}
                visualOnly={copyApproved}
                onClose={() => setComposeIndex(null)}
              />
            </div>
          </div>
        ) : (
        <div className="space-y-5">
        <Card id="render" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">Obrazki i opis</CardTitle>
            <p className="text-sm text-muted-foreground">
              {hasComposedSlides || renderRunning
                ? "Karuzela wypełnia się w trakcie składu. Po gotowym klik w slajd otwiera edytor."
                : "Po złożeniu kanon widać na pasku poniżej. Klik w slajd otwiera edytor."}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {!hasCopySlides ? (
              <p className="text-sm text-slate-600">
                Najpierw złóż treść w Przetwarzaniu (Generuj treść albo Zredaguj slajdy). Tu pojawi się trop: złóż PNG, ewentualnie popraw serię, zatwierdź.
              </p>
            ) : null}

            {hasCopySlides ? (
              <RenderJobInline
                kind={renderTabJob}
                item={item}
                params={typeof aiJobParams === "function" ? aiJobParams() : []}
                running={renderTabJobRunning}
                failed={Boolean(
                  (renderTabJob === "render" && (item?.render_status === "failed" || renderM.isError)) ||
                    (renderTabJob && renderTabJob !== "render" && (aiFailed || item?.llm_status === "error")),
                )}
                statusLabel={
                  renderTabJob === "render"
                    ? item?.render_status === "failed"
                      ? item?.render_error || "Błąd składu"
                      : renderRunning
                        ? "Składam PNG…"
                        : item?.render_status === "done"
                          ? "Obrazki gotowe"
                          : typeof aiStatusLabel === "function"
                            ? aiStatusLabel()
                            : ""
                    : typeof aiStatusLabel === "function"
                      ? aiStatusLabel()
                      : ""
                }
                error={
                  renderTabJob === "render"
                    ? renderM.isError
                      ? (renderM.error as Error)?.message
                      : item?.render_error
                    : aiError
                }
                abortPending={
                  renderTabJob === "render" ? cancelRenderM.isPending : abortLlmM?.isPending
                }
                onAbort={aiAbort}
              />
            ) : null}

            {showLiveCarousel ? (
              <LiveSlideCarousel
                item={item}
                total={slideTotal}
                done={item?.render_done}
                running={renderRunning}
                onSelect={(index) => {
                  setComposeIndex(index);
                  setStudioTab("render");
                }}
              />
            ) : null}

            {item && hasComposedSlides && !renderRunning ? <HouseCanonBar item={item} /> : null}

            {hasCopySlides && (!hasComposedSlides || houseCanon.stale) ? (
              <div id="studio-primary-cta" className="space-y-2">
                <AiActionButton
                  className="w-auto"
                  label="Złóż obrazki"
                  io="render"
                  running={renderRunning}
                  failed={item?.render_status === "failed" || renderM.isError}
                  disabled={!item || !hasCopySlides || Boolean(copyRenderBlockedReason(item))}
                  onClick={() =>
                    clickAi("render", () =>
                      renderM.mutate((item?.bg_mode || bgMode || "flat") === "flat" || !studio?.gemini_ready),
                    )
                  }
                />
                {copyRenderBlockedReason(item) ? (
                  <OpCallout variant="warning" title="Blokada składu" showLabel={false} className="text-xs">
                    Najpierw wdróż poprawki z recenzji copy, potem złóż obrazki.
                  </OpCallout>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Złoży tło, zdania zamiast VERSALIKÓW, grube hasła, jedną oś i strzałki. Marker zostawiamy na maksimum jednej ścianie.
                  </p>
                )}
                <CtaIo io="render" />
              </div>
            ) : null}

            {hasComposedSlides && !houseCanon.stale ? (
              <div id="studio-primary-cta" className="flex flex-wrap items-start gap-3">
                <Button
                  size="sm"
                  variant={houseCanon.ok ? "outline" : "brand"}
                  disabled={!item || designFixRunning || renderRunning}
                  onClick={() => clickAi("houseFix", () => designFixM.mutate())}
                >
                  {designFixRunning ? "Poprawiam serię…" : "Popraw serię"}
                </Button>
                {houseCanon.ok ? (
                  <Button
                    size="sm"
                    variant="brand"
                    disabled={!item || !copyApproved || recomposeM.isPending || approveM.isPending}
                    onClick={() => {
                      setFinalPreview("approve");
                      recomposeM.mutate();
                    }}
                  >
                    Zatwierdź do publikacji
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className={cn("space-y-4 transition-opacity", toolsDimmed && "pointer-events-none opacity-45")}>
            {hasCopySlides ? (
              <div className="space-y-2">
                <CaptionChatPanel
                  caption={caption || item?.caption || ""}
                  engage={item?.caption_engage || ""}
                  fbEnding={platformEnding("fb", item?.short_url || item?.landing_url || "")}
                  igEnding={platformEnding("ig")}
                  messages={item?.discussion?.caption_chat || []}
                  verifyCta={verifyCta}
                  busy={captionRunning}
                  canUndo={Boolean(item?.discussion?.caption_chat_undo)}
                  disabled={!item || !hasCopySlides || (copyBusy && !captionRunning)}
                  llmProgress={item?.llm_progress}
                  llmStatus={item?.llm_status}
                  error={
                    (captionM.isError && (captionM.error as Error)?.message) ||
                    (captionChatUndoM?.isError && (captionChatUndoM.error as Error)?.message) ||
                    ""
                  }
                  onCaptionChange={setCaption}
                  onCaptionCommit={(next) => {
                    if (!selectedId) return;
                    const prev = String(item?.caption || "");
                    if (next.trim() === prev.trim()) return;
                    void api.carouselPatchCopy(selectedId, { caption: next });
                  }}
                  onVerifyCta={setVerifyCta}
                  onSend={(message) => {
                    setCaptionFeedback(message);
                    clickAi("caption", () => captionM.mutate(message));
                  }}
                  onUndo={() => captionChatUndoM.mutate()}
                  undoPending={captionChatUndoM?.isPending}
                />
                <CtaIo io="caption" />
              </div>
            ) : null}
            {item?.landing_url && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Link2 className="h-3.5 w-3.5" />
                <span className="truncate">{item.short_url || item.landing_url}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigator.clipboard.writeText(item.short_url || item.landing_url)}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            {item && hasComposedSlides ? (
              <CarouselReelPanel
                item={item}
                music={musicStatusQ.data || studio?.publish?.epidemic}
                ffmpeg={studio?.publish?.ffmpeg !== false}
                tracks={searchMusicM.data?.tracks || proposeMusicQ.data?.tracks || []}
                term={searchMusicM.data?.term || proposeMusicQ.data?.term}
                loading={proposeMusicQ.isFetching && !searchMusicM.data}
                searchPending={searchMusicM.isPending}
                composePending={reelRunning}
                onSearch={(q) => searchMusicM.mutate(q)}
                onCompose={(trackId) => clickAi("reel", () => reelM.mutate(trackId))}
              />
            ) : null}
            {hasCopySlides ? (
              <StudioOptionCard
                title="Tło"
                hint="Hint działa przy Gemini. Druk go ignoruje. Zdjęcie z pickera zawsze wygrywa."
              >
                <p className="text-xs text-muted-foreground">{STUDIO_TIPS.hookVisual}</p>
                <div className="flex flex-wrap gap-2">
                  <Chip
                    active={(item?.bg_mode || bgMode || "flat") === "flat"}
                    onClick={() => templateM.mutate({ bg_mode: "flat" })}
                    tip="Płaski papier + ziarno. Bez Gemini. Zdjęcie z pickera wygrywa."
                  >
                    Druk
                  </Chip>
                  <Chip
                    active={(item?.bg_mode || bgMode) === "gemini"}
                    onClick={() =>
                      templateM.mutate({
                        bg_mode: "gemini",
                        bg_unique_count:
                          item?.bg_unique_count === 0 || item?.bg_unique_count === 2
                            ? item.bg_unique_count
                            : 1,
                      })
                    }
                    tip="Jedno tło Gemini na zestaw. Model tylko na żądanie."
                  >
                    Gemini
                  </Chip>
                </div>
                {(item?.bg_mode || bgMode) === "gemini" ? (
                  <div className="flex flex-wrap gap-2">
                    <Chip
                      active={(item?.bg_unique_count ?? 1) === 1}
                      onClick={() => templateM.mutate({ bg_unique_count: 1 })}
                      tip="Jedno tło na wszystkie slajdy. Diagram i zdjęcie z biblioteki zostają osobne."
                    >
                      1 tło
                    </Chip>
                    <Chip
                      active={(item?.bg_unique_count ?? 1) === 2}
                      onClick={() => templateM.mutate({ bg_unique_count: 2 })}
                      tip="Dwa tła: pierwsza połowa slajdów i druga."
                    >
                      2 tła
                    </Chip>
                    <Chip
                      active={(item?.bg_unique_count ?? 1) === 0}
                      onClick={() => templateM.mutate({ bg_unique_count: 0 })}
                      tip="Osobne tło na każdy slajd. Psuje spójność zestawu. Unikaj."
                    >
                      każdy slajd
                    </Chip>
                  </div>
                ) : null}
                {copySlides.some((s) => String(s.type || "").toLowerCase() === "diagram") ? (
                  <p className="text-[11px] text-muted-foreground">Ten zestaw ma diagram, to tło będzie osobne.</p>
                ) : null}
                <div className="min-w-0 space-y-3">
                  <BgPresetPicker
                    presets={catalog?.bg_presets || []}
                    value={galleryPresetId}
                    mode={activeStyleMode}
                    applyDirty={stylePresetDirty}
                    applyBusy={templateM.isPending}
                    onApply={() => {
                      if (!selectedId || pickedPresetId === null) return;
                      const preset = (catalog?.bg_presets || []).find((p) => p.id === pickedPresetId);
                      const body = buildTemplatePatchFromPreset(activeStyleMode, pickedPresetId, preset);
                      if (body.bg_preset_id != null) setBgPresetId(body.bg_preset_id);
                      if (body.style_id) {
                        setStyleId(body.style_id);
                      }
                      templateM.mutate(body, {
                        onSuccess: () => setPickedPresetId(null),
                      });
                    }}
                    onModeChange={(m) => {
                      setStyleApplyMode(m);
                      setPickedPresetId(null);
                      if (selectedId) templateM.mutate({ style_apply_mode: m });
                    }}
                    onChange={(id) => {
                      setPickedPresetId(id);
                    }}
                  />
                  <BgPlacementPicker
                    placements={catalog?.bg_placements || []}
                    value={item?.bg_placement_id || bgPlacementId}
                    onChange={(id) => {
                      setBgPlacementId(id);
                      if (selectedId) templateM.mutate({ bg_placement_id: id });
                    }}
                  />
                  {item ? (
                    <LayoutTemplateBar
                      item={item}
                      disabled={templateM.isPending || renderRunning}
                    />
                  ) : null}
                </div>
                <textarea
                  className="min-h-[72px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
                  value={bgHint}
                  maxLength={400}
                  onChange={(e) => setBgHint(e.target.value)}
                  onBlur={() => {
                    if (selectedId && bgHint !== (item?.bg_hint || "")) {
                      templateM.mutate({ bg_hint: bgHint });
                    }
                  }}
                  placeholder="np. ciepłe okno z lewej, Kasia z boku kadru, bez kuchni"
                />
                <label className="flex cursor-pointer items-start gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(item?.bg_hint_priority)}
                    disabled={!item || (!bgHint.trim() && !item?.bg_hint_priority)}
                    onChange={(e) => {
                      const on = e.target.checked;
                      const body: { bg_hint_priority: boolean; bg_hint?: string } = {
                        bg_hint_priority: on,
                      };
                      if (bgHint !== (item?.bg_hint || "")) body.bg_hint = bgHint;
                      templateM.mutate(body);
                    }}
                  />
                  <span>
                    Mój opis ma priorytet (preset i umiejscowienie schodzą na drugi plan przy Gemini).
                  </span>
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!item}
                  onClick={() => templateM.mutate({ bg_hint: bgHint })}
                >
                  Zapisz hint tła
                </Button>
              </StudioOptionCard>
            ) : null}
            {item && hasComposedSlides ? (
              <DesignAnalysisCard
                item={item}
                pending={designReviewM.isPending || item.render_status === "design"}
                applyPending={designApplyM.isPending}
                onReview={() => clickAi("designReview", () => designReviewM.mutate())}
                onApply={(selected) => clickAi("designApply", () => designApplyM.mutate(selected))}
                extraAction={
                  !houseCanon.stale ? (
                    <AiActionButton
                      className="w-auto"
                      label="Złóż od nowa"
                      io="render"
                      variant="outline"
                      running={renderRunning}
                      failed={item?.render_status === "failed" || renderM.isError}
                      disabled={!item || !hasCopySlides}
                      onClick={() =>
                        clickAi("render", () =>
                          renderM.mutate((item?.bg_mode || bgMode || "flat") === "flat" || !studio?.gemini_ready),
                        )
                      }
                    />
                  ) : null
                }
              />
            ) : null}
            {item && hasComposedSlides ? (
              <DesignLayoutNoteCard
                item={item}
                feedbackPending={designFeedbackM.isPending}
                onFeedback={(rating, text) => designFeedbackM.mutate({ rating })}
              />
            ) : null}
            {!!item?.lessons?.length && (
              <StudioOptionCard title="Wnioski retro" hint="Lekcja po publikacji. Zatwierdź albo odrzuć.">
                {item.retro?.decision ? <p className="text-xs">{item.retro.decision}</p> : null}
                <div className="space-y-2">
                  {item.lessons.map((les) => (
                    <div key={les.id} className="flex items-start justify-between gap-2 text-xs">
                      <span>
                        <Badge variant="outline">{les.status}</Badge>{" "}
                        <Badge variant="outline">{les.kind}</Badge> {les.text}
                      </span>
                      {les.status === "pending" && (
                        <span className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            className="underline"
                            onClick={() =>
                              api.carouselLessonPatch(les.id, { status: "approved" }).then(() =>
                                qc.invalidateQueries({ queryKey: ["carousel", selectedId] }),
                              )
                            }
                          >
                            Zatwierdź
                          </button>
                          <button
                            type="button"
                            className="underline"
                            onClick={() =>
                              api.carouselLessonPatch(les.id, { status: "rejected" }).then(() =>
                                qc.invalidateQueries({ queryKey: ["carousel", selectedId] }),
                              )
                            }
                          >
                            Odrzuć
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </StudioOptionCard>
            )}
            </div>
            <CostFooter item={item} />
          </CardContent>
        </Card>
        </div>
        )}
        </TabsContent>

  );
}

